import React, { useState, useEffect } from 'react';
import { 
  Megaphone, 
  Plus, 
  Edit2, 
  Trash2, 
  AlertCircle, 
  Info, 
  AlertTriangle,
  Bell,
  Calendar,
  User,
  Send
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { hasPermission } from '../../utils/helpers';

const AnnouncementsView = () => {
  const { currentUser } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    importance: 'info',
    sendNotification: true
  });
  const [saving, setSaving] = useState(false);

  // Permissions
  const canManage = hasPermission(currentUser, 'manage_announcements') || 
                     currentUser?.role === 'admin' || 
                     currentUser?.role === 'pasteur' || 
                     currentUser?.role === 'president';

  useEffect(() => {
    loadAnnouncements();
  }, []);

  const loadAnnouncements = async () => {
    try {
      const q = query(collection(db, 'announcements'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAnnouncements(data);
    } catch (error) {
      logger.error('Erreur lors du chargement des annonces:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier que l'utilisateur est connecté
    if (!currentUser?.uid) {
      alert('❌ Erreur: Vous devez être connecté pour créer une annonce');
      return;
    }
    
    if (!formData.title.trim() || !formData.content.trim()) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      const announcementData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        importance: formData.importance,
        authorId: currentUser.uid,
        authorName: currentUser.name || currentUser.displayName || currentUser.email || 'Utilisateur',
        updatedAt: serverTimestamp()
      };

      if (editingAnnouncement) {
        // Modifier une annonce existante
        await updateDoc(doc(db, 'announcements', editingAnnouncement.id), announcementData);
        alert('✅ Annonce modifiée avec succès !');
      } else {
        // Créer une nouvelle annonce
        announcementData.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(db, 'announcements'), announcementData);
        
        // Envoyer une notification IN-APP si demandé
        if (formData.sendNotification) {
          await sendInAppNotification({
            ...announcementData,
            id: docRef.id,
            createdAt: new Date()
          });
        }
        
        alert('✅ Annonce créée avec succès !');
      }

      // Réinitialiser le formulaire
      setFormData({
        title: '',
        content: '',
        importance: 'info',
        sendNotification: true
      });
      setShowForm(false);
      setEditingAnnouncement(null);
      
      // Recharger les annonces
      loadAnnouncements();
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde de l\'annonce');
    } finally {
      setSaving(false);
    }
  };

  // 🔔 NOTIFICATION IN-APP (compatible avec NotificationContext existant)
  const sendInAppNotification = async (announcement) => {
    try {
      // Créer une notification in-app dans Firestore
      // Structure compatible avec NotificationContext
      const notificationData = {
        type: 'announcement',
        title: announcement.title,
        message: announcement.content,
        importance: announcement.importance,
        announcementId: announcement.id,
        targetUsers: ['all'], // 📢 Pour tous les utilisateurs
        targetRoles: [], // Pas de ciblage par rôle
        readBy: [], // 📝 Aucun utilisateur n'a lu pour le moment
        createdAt: new Date(),
        targetRoute: '/announcements', // Redirection au clic
        metadata: {
          authorId: announcement.authorId,
          authorName: announcement.authorName
        }
      };

      await addDoc(collection(db, 'notifications'), notificationData);
      logger.log('? Notification in-app créée pour tous les utilisateurs');

      // 🎁 BONUS : Notification du navigateur (si autorisé)
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(announcement.title, {
          body: announcement.content.substring(0, 100) + (announcement.content.length > 100 ? '...' : ''),
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: 'announcement-' + announcement.id
        });
      } else if ('Notification' in window && Notification.permission === 'default') {
        // Demander la permission si pas encore demandée
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            new Notification(announcement.title, {
              body: announcement.content.substring(0, 100),
              icon: '/logo192.png'
            });
          }
        });
      }

      return true;
    } catch (error) {
      logger.error('? Erreur lors de l\'envoi de la notification:', error);
      // Ne pas bloquer la création de l'annonce
      return false;
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      importance: announcement.importance,
      sendNotification: false
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette annonce ?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'announcements', id));
      alert('✅ Annonce supprimée avec succès !');
      loadAnnouncements();
    } catch (error) {
      logger.error('Erreur lors de la suppression:', error);
      alert('❌ Erreur lors de la suppression de l\'annonce');
    }
  };

  const getImportanceConfig = (importance) => {
    switch (importance) {
      case 'urgent':
        return {
          icon: AlertTriangle,
          color: 'red',
          bgColor: 'bg-red-50 dark:bg-red-900/20',
          borderColor: 'border-red-200 dark:border-red-800',
          textColor: 'text-red-800 dark:text-red-200',
          badgeColor: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400',
          label: '🚨 Urgent'
        };
      case 'important':
        return {
          icon: AlertCircle,
          color: 'orange',
          bgColor: 'bg-orange-50 dark:bg-orange-900/20',
          borderColor: 'border-orange-200 dark:border-orange-800',
          textColor: 'text-orange-800 dark:text-orange-200',
          badgeColor: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400',
          label: '⚠️ Important'
        };
      default:
        return {
          icon: Info,
          color: 'blue',
          bgColor: 'bg-blue-50 dark:bg-blue-900/20',
          borderColor: 'border-blue-200 dark:border-blue-800',
          textColor: 'text-blue-800 dark:text-blue-200',
          badgeColor: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400',
          label: 'ℹ️ Information'
        };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
            <Megaphone size={32} className="text-blue-600" />
            Annonces de l'Église
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Communications importantes pour les membres
          </p>
        </div>

        {canManage && !showForm && (
          <button
            onClick={() => {
              setShowForm(true);
              setEditingAnnouncement(null);
              setFormData({
                title: '',
                content: '',
                importance: 'info',
                sendNotification: true
              });
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            <Plus size={20} />
            Nouvelle annonce
          </button>
        )}
      </div>

      {/* Formulaire */}
      {showForm && canManage && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-2 border-blue-200 dark:border-blue-800">
          <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
            {editingAnnouncement ? 'Modifier l\'annonce' : 'Créer une annonce'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Titre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Titre *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Ex: Culte de jeûne ce vendredi"
                required
              />
            </div>

            {/* Contenu */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Contenu *
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={5}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Détails de l'annonce..."
                required
              />
            </div>

            {/* Importance */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Niveau d'importance
              </label>
              <select
                value={formData.importance}
                onChange={(e) => setFormData({ ...formData, importance: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="info">ℹ️ Information</option>
                <option value="important">⚠️ Important</option>
                <option value="urgent">🚨 Urgent</option>
              </select>
            </div>

            {/* Envoyer notification (seulement en création) */}
            {!editingAnnouncement && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <input
                  type="checkbox"
                  id="sendNotification"
                  checked={formData.sendNotification}
                  onChange={(e) => setFormData({ ...formData, sendNotification: e.target.checked })}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="sendNotification" className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-200 cursor-pointer">
                  <Bell size={18} />
                  Notifier tous les membres (notification in-app)
                </label>
              </div>
            )}

            {/* Boutons */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingAnnouncement(null);
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    {editingAnnouncement ? 'Modifier' : 'Publier'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Liste des annonces */}
      <div className="space-y-4">
        {announcements.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
            <Megaphone className="mx-auto text-gray-400 mb-4" size={64} />
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              Aucune annonce pour le moment
            </p>
          </div>
        ) : (
          announcements.map((announcement) => {
            const config = getImportanceConfig(announcement.importance);
            const Icon = config.icon;
            
            return (
              <div
                key={announcement.id}
                className={`${config.bgColor} border ${config.borderColor} rounded-lg p-6 shadow-sm`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* En-tête */}
                    <div className="flex items-center gap-3 mb-3">
                      <Icon size={24} className={config.textColor} />
                      <span className={`px-3 py-1 ${config.badgeColor} text-xs font-semibold rounded-full`}>
                        {config.label}
                      </span>
                    </div>

                    {/* Titre */}
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
                      {announcement.title}
                    </h3>

                    {/* Contenu */}
                    <p className="text-gray-700 dark:text-gray-300 mb-4 whitespace-pre-wrap">
                      {announcement.content}
                    </p>

                    {/* Métadonnées */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        <User size={14} />
                        {announcement.authorName}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        {announcement.createdAt?.toDate ? 
                          announcement.createdAt.toDate().toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : 
                          'Date inconnue'
                        }
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  {canManage && (
                    <div className="flex gap-2 ml-4">
                      <button
                        onClick={() => handleEdit(announcement)}
                        className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(announcement.id)}
                        className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AnnouncementsView;
